import React from 'react';
import type { ComplianceConsoleKind } from '../constants/complianceChecklists';
import DodTermText from '../DodTermText';
import './DodConsoleGuide.css';

export const DOD_GUIDE_STORAGE_KEY = 'nextcompli.console.dod-contracts-compliance.guide.v1';

export const guideStorageKeyFor = (kind: ComplianceConsoleKind): string => (
  `nextcompli.console.${kind}.guide.v1`
);

type DodConsoleGuideProps = {
  language: 'en' | 'es';
  hasCompany: boolean;
  addingCompany: boolean;
  companyCount: number;
  kind?: ComplianceConsoleKind;
  /** Only when creating/editing a government project — not on the empty console landing. */
  showDataTypeNavigator?: boolean;
  selectedHandling?: 'none' | 'fci' | 'cui' | 'classified' | '';
  onNavigatorChoice?: (choice: 'fci' | 'cui' | 'unsure') => void;
};

type GuideStep = {
  titleEn: string;
  titleEs: string;
  bodyEn: string;
  bodyEs: string;
};

const DOD_STEPS: readonly GuideStep[] = [
  {
    titleEn: 'Pick the company',
    titleEs: 'Elija el proyecto',
    bodyEn: 'This page is one project’s homework. Every project gets the same Government Compliance binder — city and county portals do not replace SAM. Use the Project menu, or press Add project and type the legal name from the tax letter.',
    bodyEs: 'Esta página es la tarea de un proyecto. Cada proyecto recibe el mismo expediente de Cumplimiento gubernamental: los portales de ciudad y condado no sustituyen SAM. Use el menú Proyecto, o pulse Añadir proyecto y escriba el nombre legal de la carta de impuestos.',
  },
  {
    titleEn: 'Do the first row',
    titleEs: 'Haga la primera fila',
    bodyEn: 'After you pick a company, the open list shows what to do now. Ignore the rest until that row is done.',
    bodyEs: 'Cuando elija una empresa, la lista abierta dice qué hacer ahora. Ignore el resto hasta terminar esa fila.',
  },
  {
    titleEn: 'Confirm the step with proof',
    titleEs: 'Confirme el paso con prueba',
    bodyEn: 'On the open row, mark each step done after you upload a screenshot or type the proof (INC- number, last-four, UEI). Do not mark the whole row complete until the final proof is on file.',
    bodyEs: 'En la fila abierta, marque cada paso cuando suba una captura o escriba la prueba (número INC-, últimos cuatro, UEI). No marque la fila completa hasta tener la prueba final.',
  },
];

const WEBSITE_STEPS: readonly GuideStep[] = [
  {
    titleEn: 'Pick the website',
    titleEs: 'Elija el sitio',
    bodyEn: 'This page is one website’s homework. Use the Website menu, or press Add website and type the public site name or domain.',
    bodyEs: 'Esta página es la tarea de un sitio. Use el menú Sitio, o pulse Añadir sitio y escriba el nombre público o el dominio.',
  },
  {
    titleEn: 'Do the first row',
    titleEs: 'Haga la primera fila',
    bodyEn: 'After you pick a website, the open list shows what to do now. Saving a URL or domain starts a homepage scan automatically. Ignore the rest of the list until that row is done.',
    bodyEs: 'Cuando elija un sitio, la lista abierta dice qué hacer ahora. Guardar una URL o dominio lanza el escaneo de la homepage. Ignore el resto hasta terminar esa fila.',
  },
  {
    titleEn: 'Confirm the step with proof',
    titleEs: 'Confirme el paso con prueba',
    bodyEn: 'Mark a step compliant after you keep a URL, screenshot, or scan finding. Do not mark the whole row compliant until that proof is on file.',
    bodyEs: 'Marque un paso como cumple cuando conserve una URL, captura o hallazgo del escaneo. No marque la fila completa hasta tener esa prueba en archivo.',
  },
];

const EDUCATION_STEPS: readonly GuideStep[] = [
  {
    titleEn: 'Pick the campus',
    titleEs: 'Elija el campus',
    bodyEn: 'This page is one campus or research office’s homework. Use the Campus menu, or press Add campus and type the institution’s legal name.',
    bodyEs: 'Esta página es la tarea de un campus u oficina de investigación. Use el menú Campus, o pulse Añadir campus y escriba el nombre legal de la institución.',
  },
  {
    titleEn: 'Do the first row',
    titleEs: 'Haga la primera fila',
    bodyEn: 'After you pick the campus, the open list shows what to do now — usually screening a scholar or partner. Ignore the rest until that row is done.',
    bodyEs: 'Cuando elija el campus, la lista abierta dice qué hacer ahora — casi siempre cribar a un académico o socio. Ignore el resto hasta terminar esa fila.',
  },
  {
    titleEn: 'Confirm the step with proof',
    titleEs: 'Confirme el paso con prueba',
    bodyEn: 'Keep the OFAC case id, HECVAT toolkit link, or owner name as proof. Do not paste licensed HECVAT questions into NextCompli.',
    bodyEs: 'Conserve el id de caso OFAC, el enlace HECVAT o el nombre del dueño como prueba. No pegue preguntas HECVAT con licencia en NextCompli.',
  },
];

const FORMATION_STEPS: readonly GuideStep[] = [
  {
    titleEn: 'Name the legal entity',
    titleEs: 'Ponga el nombre de la entidad legal',
    bodyEn: 'The page opens on the Legal entity menu. Add the legal entity, or pick one that is already saved. The questions stay hidden until that name is saved.',
    bodyEs: 'La página abre en el menú Entidad legal. Añada la entidad legal, o elija una ya guardada. Las preguntas quedan ocultas hasta que ese nombre se guarde.',
  },
  {
    titleEn: 'Answer one question at a time',
    titleEs: 'Responda una pregunta a la vez',
    bodyEn: 'The first question is the kind of organization. Each answer opens the next one. You can go back. The legal name is already saved.',
    bodyEs: 'La primera pregunta es el tipo de negocio. Cada respuesta abre la siguiente. Puede volver atrás. El nombre de la empresa ya está guardado.',
  },
  {
    titleEn: 'Do the first row',
    titleEs: 'Haga la primera fila',
    bodyEn: 'When the questions end, the open list says what to save. Mark a step finished only after that record is in the company file.',
    bodyEs: 'Cuando terminan las preguntas, la lista abierta dice qué presentar o guardar. Archive la prueba solo cuando esté en el expediente.',
  },
];

const WILLS_STEPS: readonly GuideStep[] = [
  {
    titleEn: 'Name the person',
    titleEs: 'Ponga el nombre de la persona',
    bodyEn: 'The page opens on the Person menu. Add the person, or pick one that is already saved. The questions stay hidden until that name is saved.',
    bodyEs: 'La página abre en el menú Persona. Añada a la persona, o elija una ya guardada. Las preguntas quedan ocultas hasta que ese nombre se guarde.',
  },
  {
    titleEn: 'Answer one question at a time',
    titleEs: 'Responda una pregunta a la vez',
    bodyEn: 'The first question is will only, or a revocable trust plus a will. Each answer opens the next one. You can go back.',
    bodyEs: 'La primera pregunta es solo testamento, o un fideicomiso revocable más un testamento. Cada respuesta abre la siguiente. Puede volver atrás.',
  },
  {
    titleEn: 'A lawyer prepares the papers',
    titleEs: 'Un abogado prepara los papeles',
    bodyEn: 'When the questions end, the list says what the lawyer prepares and what to keep. NextCompli does not write the will or the trust, and it does not file them.',
    bodyEs: 'Cuando terminan las preguntas, la lista dice qué prepara el abogado y qué hay que guardar. NextCompli no redacta el testamento ni el fideicomiso, y no los presenta.',
  },
];

const CLOUD_STEPS: readonly GuideStep[] = [
  {
    titleEn: 'Pick the subscription account',
    titleEs: 'Elija la cuenta de suscripción',
    bodyEn: 'The client work stays hidden until an account is chosen.',
    bodyEs: 'El trabajo del cliente permanece oculto hasta que elija una cuenta.',
  },
  {
    titleEn: 'Name the client cloud account',
    titleEs: 'Nombre la cuenta de nube del cliente',
    bodyEn: 'This is the client’s account, not the account that runs NextCompli. Website Compliance still crawls the public site without this access.',
    bodyEs: 'Esta es la cuenta del cliente, no la cuenta donde corre NextCompli. Cumplimiento del sitio web sigue recorriendo el sitio público sin este acceso.',
  },
  {
    titleEn: 'Record the read-only access',
    titleEs: 'Registre el acceso de solo lectura',
    bodyEn: 'Save who granted it and the date. Then work the written checklist: sign-in, audit logs, network, storage, backups, and findings. Do not put passwords or secret keys in the checklist.',
    bodyEs: 'Guarde quién lo concedió y la fecha. Luego haga la lista escrita: inicio de sesión, registros, red, almacenamiento, copias y hallazgos. No ponga contraseñas ni claves secretas en la lista.',
  },
  {
    titleEn: 'Queue the daily review on CompliAdvantage',
    titleEs: 'Ponga en cola la revisión diaria en CompliAdvantage',
    bodyEn: 'CompliAdvantage can queue one read-only review a day. It writes findings. A person files proof. It does not change the account.',
    bodyEs: 'CompliAdvantage puede poner en cola una revisión de solo lectura al día. Escribe hallazgos. Una persona archiva la prueba. No cambia la cuenta.',
  },
];

const OFAC_STEPS: readonly GuideStep[] = [
  {
    titleEn: 'Pick the screening account',
    titleEs: 'Elija la cuenta de cribado',
    bodyEn: 'The checklist stays hidden until the account is chosen. Screening and the whitelist use that same account.',
    bodyEs: 'La lista permanece oculta hasta que elija la cuenta. Screening y la lista blanca usan esa misma cuenta.',
  },
  {
    titleEn: 'Refresh the lists, then screen',
    titleEs: 'Actualice las listas y luego cribe',
    bodyEn: 'Confirm the SDN and CAPTA refresh, import the names, and run Screen now. The review queue waits on that screen.',
    bodyEs: 'Confirme la actualización de SDN y CAPTA, importe los nombres y ejecute Screen now. La cola de revisión espera ese cribado.',
  },
  {
    titleEn: 'Decide each possible match',
    titleEs: 'Decida cada posible coincidencia',
    bodyEn: 'Clear a match with a reason, or escalate it. An investigation and a whitelist entry both wait on that decision.',
    bodyEs: 'Descarte una coincidencia con un motivo, o escálela. Una investigación y una entrada en la lista blanca esperan esa decisión.',
  },
];

const stepsFor = (kind: ComplianceConsoleKind): readonly GuideStep[] => {
  if (kind === 'website-compliance') return WEBSITE_STEPS;
  if (kind === 'education-compliance') return EDUCATION_STEPS;
  if (kind === 'business-creation-compliance') return FORMATION_STEPS;
  if (kind === 'wills-trusts-compliance') return WILLS_STEPS;
  if (kind === 'cloud-compliance') return CLOUD_STEPS;
  if (kind === 'ofac-compliance') return OFAC_STEPS;
  return DOD_STEPS;
};

const DodConsoleGuide: React.FC<DodConsoleGuideProps> = ({
  language,
  kind = 'dod-contracts-compliance',
  showDataTypeNavigator = false,
  selectedHandling = '',
  onNavigatorChoice,
}) => {
  const spanish = language === 'es';
  const steps = stepsFor(kind);
  const storageKey = guideStorageKeyFor(kind);
  const [tourOpen, setTourOpen] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [navigatorChoice, setNavigatorChoice] = React.useState<'fci' | 'cui' | 'unsure' | ''>(() => {
    if (selectedHandling === 'fci' || selectedHandling === 'cui') {
      return selectedHandling;
    }
    if (selectedHandling === 'none') {
      return 'unsure';
    }
    try {
      const stored = window.localStorage.getItem('nextcompli.dod.navigator');
      return stored === 'fci' || stored === 'cui' || stored === 'unsure' ? stored : '';
    } catch {
      return '';
    }
  });
  React.useEffect(() => {
    if (selectedHandling === 'fci' || selectedHandling === 'cui') {
      setNavigatorChoice(selectedHandling);
      return;
    }
    if (selectedHandling === 'none') {
      setNavigatorChoice('unsure');
    }
  }, [selectedHandling]);
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  const closeTour = React.useCallback(() => {
    setTourOpen(false);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ seen: true }));
    } catch {
      /* ignore quota / private mode */
    }
  }, [storageKey]);

  const openTour = React.useCallback(() => {
    setStepIndex(0);
    setTourOpen(true);
  }, []);

  React.useEffect(() => {
    if (!tourOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeTour();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const firstButton = dialogRef.current?.querySelector('button');
    firstButton?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [tourOpen, closeTour, stepIndex]);

  const step = steps[stepIndex];

  return (
    <div className="dod-guide">
      {kind === 'dod-contracts-compliance' && showDataTypeNavigator ? (
        <div className="dod-start-card">
          <div className="dod-start-card-copy">
            <h3>{spanish ? 'Qué papeles usa este contrato?' : 'What papers does this contract use?'}</h3>
            <p>
              <DodTermText
                language={spanish ? 'es' : 'en'}
                seen={new Set()}
                text={
                  spanish
                    ? 'Elija una vez. FCI es el papelerío cotidiano. CUI es extra secreto no clasificado — NextCompli no aloja CUI. Si no sabe, elija la empresa primero.'
                    : 'Pick once. FCI is everyday contract paperwork. CUI is extra-secret unclassified — NextCompli does not host CUI. If you are not sure, pick the company first.'
                }
              />
            </p>
          </div>
          <div className="dod-navigator" role="group" aria-label={spanish ? 'Tipo de datos' : 'Data type'}>
            {([
              { id: 'fci', en: 'Everyday papers (FCI)', es: 'Papeles cotidianos (FCI)' },
              { id: 'cui', en: 'Extra-secret unclassified (CUI)', es: 'Extra secreto no clasificado (CUI)' },
              { id: 'unsure', en: 'Not sure yet', es: 'Aún no sé' },
            ] as const).map((choice) => (
              <button
                key={choice.id}
                type="button"
                className={`dod-navigator-btn ${navigatorChoice === choice.id ? 'is-selected' : ''}`}
                onClick={() => {
                  setNavigatorChoice(choice.id);
                  try {
                    window.localStorage.setItem('nextcompli.dod.navigator', choice.id);
                  } catch {
                    /* ignore */
                  }
                  onNavigatorChoice?.(choice.id);
                }}
              >
                {spanish ? choice.es : choice.en}
              </button>
            ))}
          </div>
          {navigatorChoice === 'cui' ? (
            <p className="dod-navigator-note">
              <DodTermText
                language={spanish ? 'es' : 'en'}
                seen={new Set()}
                text={
                  spanish
                    ? 'Las herramientas CUI aparecen solo cuando un aviso lo exige. No suba CUI a NextCompli.'
                    : 'CUI tools appear only when a notice requires them. Do not upload CUI into NextCompli.'
                }
              />
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="dod-guide-toolbar">
        <button
          type="button"
          className="dod-guide-replay compliance-icon-btn"
          aria-label={spanish ? 'Cómo usar esta página' : 'How to use this page'}
          onClick={openTour}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 4.5h11a2 2 0 0 1 2 2V19l-3.2-1.6L12.5 19 9.2 17.4 6 19V6.5a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M9 9h6M9 12.5h6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {tourOpen ? (
        <div className="dod-guide-backdrop">
          <div
            ref={dialogRef}
            className="dod-guide-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dod-guide-title"
          >
            <p className="dod-guide-kicker">
              <span className="dod-start-step-num" aria-hidden="true">{stepIndex + 1}</span>
              {spanish ? `Paso ${stepIndex + 1} de ${steps.length}` : `Step ${stepIndex + 1} of ${steps.length}`}
            </p>
            <h3 id="dod-guide-title">{spanish ? step.titleEs : step.titleEn}</h3>
            <p><DodTermText text={spanish ? step.bodyEs : step.bodyEn} language={spanish ? 'es' : 'en'} /></p>
            <div className="dod-guide-progress" aria-hidden="true">
              {steps.map((entry, index) => (
                <span key={entry.titleEn} className={index === stepIndex ? 'is-active' : undefined} />
              ))}
            </div>
            <div className="dod-guide-actions">
              <button type="button" className="dashboard-security-inline-btn" onClick={closeTour}>
                {spanish ? 'Saltar' : 'Skip'}
              </button>
              {stepIndex > 0 ? (
                <button
                  type="button"
                  className="dashboard-security-inline-btn"
                  onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
                >
                  {spanish ? 'Atrás' : 'Back'}
                </button>
              ) : null}
              {stepIndex < steps.length - 1 ? (
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary"
                  onClick={() => setStepIndex((current) => Math.min(steps.length - 1, current + 1))}
                >
                  {spanish ? 'Siguiente' : 'Next'}
                </button>
              ) : (
                <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={closeTour}>
                  {spanish ? 'Entendido' : 'Got it'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DodConsoleGuide;
